import React from "react";
import {
  Card,
  Flex,
  Text,
  Badge,
  Divider,
  Loader,
  ScrollArea,
  Stack,
  Paper,
  Image,
  Box,
  Tooltip,
  ActionIcon,
  Modal,
  Group,
  Button,
} from "@mantine/core";
import { IconX, IconAlertTriangle, IconTrash } from "@tabler/icons-react";
import NoImage from "../../../../assets/img/images/no-images.png";

const SelectedProductsPreview = ({
  products,
  isLoading,
  tagList,
  countryList,
  handleRemoveProduct,
  deleteModalOpened,
  closeDeleteModal,
  deleting,
  handleDelete,
}) => {
  return (
    <Card withBorder radius="md" padding="lg" h="100%">
      <Flex justify="space-between" align="center" mb={12}>
        <Text fw={600} size="sm">
          Selected Products
          <Badge ml={8} color="blue" variant="light">
            {isLoading ? "…" : products.length}
          </Badge>
        </Text>
      </Flex>
      <Divider mb={12} />

      {isLoading ? (
        <Flex
          justify="center"
          align="center"
          py={40}
          direction="column"
          gap={8}
        >
          <Loader size={24} />
          <Text size="xs" c="dimmed">
            Loading products…
          </Text>
        </Flex>
      ) : (
        <ScrollArea h={560} type="auto">
          <Stack gap={8}>
            {products.map((product) => (
              <Paper key={product.id} withBorder p="sm" radius="sm">
                <Flex align="center" gap="sm">
                  <Image
                    src={product.image || NoImage}
                    fallbackSrc={NoImage}
                    w={52}
                    h={52}
                    radius="sm"
                    fit="cover"
                    style={{ flexShrink: 0 }}
                  />
                  <Box style={{ flex: 1, minWidth: 0 }}>
                    <Text size="xs" fw={600} lineClamp={2}>
                      {product.title}
                    </Text>
                    <Text size="10px" c="dimmed" mt={2}>
                      ID: {product.id}
                    </Text>
                    {/* Current values hints */}
                    <Group gap={4} mt={4} wrap="wrap">
                      {/* Category Badge */}
                      {product.currentCategories &&
                        product.currentCategories.map(
                          (c) => (
                            <Badge
                              key={c.id}
                              size="xs"
                              variant="light"
                              color="blue"
                            >
                              Category: {c.name}
                            </Badge>
                          ),
                        )}

                      {/* Weight Badge */}
                      {product.currentWeight > 0 && (
                        <Badge
                          size="xs"
                          variant="light"
                          color="orange"
                        >
                          Weight: {product.currentWeight}{" "}
                          {product.currentWeightUnit}
                        </Badge>
                      )}

                      {/* Tags/Labels Badges */}
                      {product.currentTags.map((t) => {
                        const found = tagList.find(
                          (tl) => tl.value === t,
                        );
                        return found ? (
                          <Badge
                            key={t}
                            size="xs"
                            variant="light"
                            color="grape"
                          >
                            {found.label}
                          </Badge>
                        ) : null;
                      })}

                      {/* Country Badge */}
                      {product.currentCountryId && (
                        <Badge size="xs" variant="light" color="teal">
                          {countryList.find(
                            (c) =>
                              c.value === product.currentCountryId,
                          )?.label ||
                            `Country #${product.currentCountryId}`}
                        </Badge>
                      )}

                      {/* Warehouse Info Badge */}
                      {product.currentWarehouses &&
                        product.currentWarehouses.map((w) => (
                          <Badge
                            key={w.id}
                            size="xs"
                            variant="filled"
                            color="indigo"
                          >
                            WH: {w.name}
                          </Badge>
                        ))}
                    </Group>
                  </Box>
                  <Tooltip label="Remove from bulk list" withArrow>
                    <ActionIcon
                      variant="subtle"
                      color="red"
                      size="sm"
                      onClick={() => handleRemoveProduct(product.id)}
                    >
                      <IconX size={14} />
                    </ActionIcon>
                  </Tooltip>
                </Flex>
              </Paper>
            ))}
          </Stack>
        </ScrollArea>
      )}

      {/* ── Delete Confirmation Modal ────────────────────────────────────── */}
      <Modal
        opened={deleteModalOpened}
        onClose={closeDeleteModal}
        title={
          <Flex align="center" gap={8}>
            <IconAlertTriangle size={20} color="red" />
            <Text fw={700} c="red">
              Confirm Bulk Delete
            </Text>
          </Flex>
        }
        centered
        size="md"
      >
        <Text mb={16}>
          You are about to <strong>permanently delete</strong> the following{" "}
          <strong>
            {products.length} product{products.length !== 1 ? "s" : ""}
          </strong>
          . This action cannot be undone.
        </Text>

        <ScrollArea h={200} mb={20}>
          <Stack gap={6}>
            {products.map((p) => (
              <Paper key={p.id} withBorder p="xs" radius="sm">
                <Flex align="center" gap="sm">
                  <Image
                    src={p.image || NoImage}
                    fallbackSrc={NoImage}
                    w={36}
                    h={36}
                    radius="sm"
                    fit="cover"
                    style={{ flexShrink: 0 }}
                  />
                  <Box style={{ minWidth: 0 }}>
                    <Text size="xs" fw={600} lineClamp={1}>
                      {p.title}
                    </Text>
                    <Text size="10px" c="dimmed">
                      ID: {p.id}
                    </Text>
                  </Box>
                </Flex>
              </Paper>
            ))}
          </Stack>
        </ScrollArea>

        <Group justify="flex-end">
          <Button
            variant="outline"
            color="gray"
            onClick={closeDeleteModal}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            color="red"
            leftSection={<IconTrash size={16} />}
            loading={deleting}
            onClick={handleDelete}
          >
            Delete {products.length} Product{products.length !== 1 ? "s" : ""}
          </Button>
        </Group>
      </Modal>
    </Card>
  );
};

export default SelectedProductsPreview;
